import type { NoteType } from '@spotnote/shared/types'

/**
 * The widget's two languages. Detected from the feedback giver's browser at
 * mount; a manual choice from the toolbar's language menu wins and is
 * remembered per site in localStorage.
 *
 * `en` is the source of truth; `de` is typed against it so a missing key is a
 * compile error, not an English string leaking into a German session.
 */

export type WidgetLocale = 'en' | 'de'

const STORAGE_KEY = 'spotnote-lang'

const en = {
  launcher: 'Feedback',
  launcherSent: '✓ Sent — add more',
  launcherAria: 'Give feedback',
  launcherAriaSent: 'Sent — add more feedback',
  launcherAriaUnsent: (n: number): string =>
    `${n} unsent ${n === 1 ? 'note' : 'notes'} — open feedback to send`,
  by: (studio: string): string => `by ${studio}`,

  toolbarAria: 'Feedback tools',
  toolNote: 'Note',
  toolHighlight: 'Highlight',
  toolSketch: 'Sketch',
  undo: 'Undo last note',
  send: 'Send',
  languageAria: 'Change language',
  exitAria: 'Exit feedback mode',
  noteCount: (n: number): string => `${n} ${n === 1 ? 'note' : 'notes'}`,

  hintPin: 'Click anywhere on the page to leave a note',
  hintRect: 'Drag to highlight an area',
  hintDraw: 'Draw on the page — release to add your note',
  hintComposer: 'Add your note, then save it',
  hintPick: 'Pick a tool to start',

  composerTitle: 'New note',
  composerAria: 'Your note',
  composerPlaceholder: 'What should change here?',
  saveNote: 'Save note',
  cancel: 'Cancel',

  typeLabel: { pin: 'Note', rect: 'Highlight', draw: 'Sketch' } as Record<NoteType, string>,
  reviewAria: 'Review and send',
  reviewTitle: 'Review & send',
  reviewFor: (studio: string): string => `for ${studio}`,
  snapshotNotice: 'A snapshot of the page is attached to every note automatically — nothing else to do.',
  back: 'Back',
  sending: 'Sending…',
  sendFeedback: 'Send feedback',

  sentAria: 'Feedback sent',
  sentTitle: 'Feedback sent!',
  sentBody: (n: number, studio: string | null): string => {
    const notes = `${n} ${n === 1 ? 'note is' : 'notes are'}`
    const recipient = studio ? ` to ${studio}` : ''
    return `Your ${notes} on the way${recipient} — they've just been notified by e-mail.`
  },
  backToSite: 'Back to the site',

  errorEnded: 'This feedback round has ended — your notes can no longer be submitted.',
  errorSend: 'That did not go through. Check your connection and try again.',
}

export type WidgetDict = typeof en

const de: WidgetDict = {
  launcher: 'Feedback',
  launcherSent: '✓ Gesendet — mehr hinzufügen',
  launcherAria: 'Feedback geben',
  launcherAriaSent: 'Gesendet — weiteres Feedback geben',
  launcherAriaUnsent: (n) =>
    `${n} ${n === 1 ? 'ungesendete Notiz' : 'ungesendete Notizen'} — Feedback öffnen, um zu senden`,
  by: (studio) => `von ${studio}`,

  toolbarAria: 'Feedback-Werkzeuge',
  toolNote: 'Notiz',
  toolHighlight: 'Markieren',
  toolSketch: 'Skizze',
  undo: 'Letzte Notiz zurücknehmen',
  send: 'Senden',
  languageAria: 'Sprache ändern',
  exitAria: 'Feedback-Modus verlassen',
  noteCount: (n) => `${n} ${n === 1 ? 'Notiz' : 'Notizen'}`,

  hintPin: 'Klicke irgendwo auf die Seite, um eine Notiz zu hinterlassen',
  hintRect: 'Ziehe, um einen Bereich zu markieren',
  hintDraw: 'Zeichne auf der Seite — loslassen fügt deine Notiz hinzu',
  hintComposer: 'Schreibe deine Notiz und speichere sie',
  hintPick: 'Wähle ein Werkzeug',

  composerTitle: 'Neue Notiz',
  composerAria: 'Deine Notiz',
  composerPlaceholder: 'Was soll sich hier ändern?',
  saveNote: 'Notiz speichern',
  cancel: 'Abbrechen',

  typeLabel: { pin: 'Notiz', rect: 'Markierung', draw: 'Skizze' },
  reviewAria: 'Prüfen und senden',
  reviewTitle: 'Prüfen & senden',
  reviewFor: (studio) => `für ${studio}`,
  snapshotNotice: 'Jede Notiz bekommt automatisch einen Schnappschuss der Seite — du musst nichts weiter tun.',
  back: 'Zurück',
  sending: 'Wird gesendet…',
  sendFeedback: 'Feedback senden',

  sentAria: 'Feedback gesendet',
  sentTitle: 'Feedback gesendet!',
  sentBody: (n, studio) => {
    const notes = n === 1 ? 'Deine Notiz ist' : `Deine ${n} Notizen sind`
    const recipient = studio ? ` zu ${studio}` : ''
    return `${notes} unterwegs${recipient} — die Benachrichtigung per E-Mail ist gerade raus.`
  },
  backToSite: 'Zurück zur Website',

  errorEnded: 'Diese Feedback-Runde ist beendet — deine Notizen können nicht mehr gesendet werden.',
  errorSend: 'Das hat nicht geklappt. Prüfe deine Verbindung und versuche es noch einmal.',
}

let current: WidgetDict = en

/** A remembered manual choice wins; otherwise the browser decides. */
export function detectWidgetLocale(): WidgetLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'en' || stored === 'de') return stored
  } catch {
    // Storage can be blocked on someone else's site; the browser language
    // below still works.
  }
  return (navigator.language ?? '').toLowerCase().startsWith('de') ? 'de' : 'en'
}

export function setWidgetLocale(locale: WidgetLocale, options?: { persist?: boolean }): void {
  current = locale === 'de' ? de : en
  if (options?.persist) {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // Best effort — the choice still holds for this page view.
    }
  }
}

/** The active dictionary. A function, so modules can import it at load time. */
export function t(): WidgetDict {
  return current
}
